import { PageHero, CTASection } from "@/components/Bits";
import { SectionLabel } from "@/components/SectionLabel";
import { Reveal } from "@/components/Reveal";
import { Link } from "react-router-dom";
import { Send, Users2, Package, FlaskConical, GraduationCap, Cloud, Rocket, Clock, ArrowUpRight, CheckCircle2 } from "lucide-react";

const STEPS = [
  {
    icon: Send, hex: "#2AA7D4",
    t: "Submit an Enrolment Request",
    d: "Begin by submitting your school's enquiry or enrolment request through our website or by contacting the THECREATOR Education team.",
  },
  {
    icon: Users2, hex: "#1B4A7A",
    t: "Leadership Consultation & Partnership Planning",
    d: "Our academic team conducts detailed discussions with your school leadership to understand your vision, evaluate requirements, and finalize the implementation plan and partnership.",
  },
  {
    icon: Package, hex: "#E8890C",
    t: "Procurement of Scientific Apparatus",
    d: "Based on your school's requirements, we finalize, purchase and provide the complete set of internationally standardized scientific apparatus and laboratory resources required for implementing the PBL-TDLE framework — end to end, so your school doesn't have to source anything on its own.",
  },
  {
    icon: FlaskConical, hex: "#0EA5A5",
    t: "Establishment of Concept Laboratories",
    d: "Science and Mathematics Concept Labs are designed and established with the required apparatus, experiments, and organizational systems to support concept development through demonstrations and student investigations.",
  },
  {
    icon: GraduationCap, hex: "#7C3AED",
    t: "Teacher Training in IDEA Pedagogy & Laboratory Management",
    d: "Teachers, laboratory staff, and academic leaders undergo comprehensive training in the IDEA Teaching Framework, laboratory operations, classroom implementation, assessment, and the PBL-TDLE methodology to ensure smooth and effective implementation.",
  },
  {
    icon: Cloud, hex: "#E11D66",
    t: "Deployment of the CoPE-App Platform",
    d: "The CoPE-App ecosystem is configured and deployed for school leaders, teachers, students, and parents, enabling lesson planning, concept mapping, assessments, problem-solving, analytics, and academic monitoring.",
  },
  {
    icon: Rocket, hex: "#0F9D58",
    t: "Launch of the PBL-TDLE Framework",
    d: "Following successful preparation, the school formally begins classroom implementation of the PBL-TDLE (Problem-Based Learning through Teaching by Demonstrations and Learning by Experiments) framework with continuous academic mentoring, implementation support, quality monitoring, and periodic reviews from THECREATOR Education.",
  },
];

export default function YourJourney() {
  return (
    <div data-testid="your-journey-page">
      <PageHero
        label="Your Journey to PBL-TDLE"
        lines={["Your Journey", "to PBL-TDLE"]}
        intro="A structured implementation process — from your first enquiry to a fully running PBL-TDLE school, guided by THECREATOR Education at every step."
      />

      {/* Timeline */}
      <section className="border-t border-white/10" data-testid="journey-timeline">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="A">A Structured Implementation Process</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            Seven steps. One transformation.
          </h2>

          <div className="mt-16 relative">
            {/* vertical spine */}
            <div className="absolute left-[27px] md:left-[35px] top-0 bottom-0 w-px bg-white/15" aria-hidden="true" />
            <ol className="space-y-8 md:space-y-10">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <Reveal key={s.t} delay={(i % 3) * 0.06}>
                    <li className="relative flex gap-6 md:gap-10" data-testid={`journey-step-${i + 1}`}>
                      <div className="relative z-10 shrink-0">
                        <div className="w-14 h-14 md:w-[72px] md:h-[72px] rounded-2xl border-2 flex items-center justify-center bg-[#050B14]" style={{ borderColor: s.hex }}>
                          <Icon className="w-6 h-6 md:w-8 md:h-8" style={{ color: s.hex }} />
                        </div>
                      </div>
                      <div className="flex-1 border border-white/10 rounded-2xl p-6 md:p-8 hover:border-accent/50 transition-colors bg-[#070d18]">
                        <div className="flex items-baseline gap-4 flex-wrap">
                          <span className="font-display text-4xl md:text-5xl leading-none" style={{ color: s.hex }}>{String(i + 1).padStart(2, "0")}</span>
                          <h3 className="font-display text-xl md:text-2xl text-white tracking-tight leading-snug">{s.t}</h3>
                        </div>
                        <p className="text-white/70 leading-relaxed mt-4 max-w-4xl">{s.d}</p>
                      </div>
                    </li>
                  </Reveal>
                );
              })}
            </ol>
          </div>
        </div>
      </section>

      {/* Closing note — 6 to 10 weeks */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="journey-closing">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <Reveal>
            <div className="rounded-3xl border dk-border dk-panel overflow-hidden grid lg:grid-cols-12">
              <div className="lg:col-span-4 p-8 md:p-12 border-b lg:border-b-0 lg:border-r dk-border flex flex-col justify-center">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-c-amber" />
                  <span className="font-mono-label text-[11px] tracking-label uppercase text-c-amber">Typical Timeline</span>
                </div>
                <div className="font-display text-[4.5rem] md:text-[6rem] leading-none dk-text mt-4">6–10</div>
                <p className="font-mono-label text-[11px] tracking-label uppercase dk-text-muted mt-2">Weeks to full implementation</p>
              </div>
              <div className="lg:col-span-8 p-8 md:p-12">
                <div className="font-mono-label text-[11px] tracking-label uppercase text-c-amber">Closing Note</div>
                <p className="dk-text-soft text-lg leading-relaxed mt-4">
                  The complete implementation process is typically completed within <span className="dk-text font-medium">6 to 10 weeks</span>, depending on the school's readiness, procurement timelines, and training schedule — after which students begin learning through the PBL-TDLE framework with <span className="dk-text font-medium">ongoing support from THECREATOR Education</span>.
                </p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <Link
                    to="/enroll"
                    data-testid="journey-enroll-cta"
                    className="inline-flex items-center gap-2 bg-accent text-[#050B14] px-6 py-3.5 rounded-full text-sm font-semibold hover:bg-accent/90 transition-colors"
                  >
                    Start Your Journey — Enroll Your School <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
                <ul className="mt-8 grid sm:grid-cols-3 gap-3">
                  {["Continuous academic mentoring", "Quality monitoring & reviews", "Implementation support"].map((b) => (
                    <li key={b} className="flex items-start gap-2 dk-text-soft text-sm leading-relaxed">
                      <CheckCircle2 className="w-4 h-4 text-c-teal shrink-0 mt-0.5" /> {b}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
